import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IressStack,
  IressInline,
  IressText,
  IressButton,
  IressPanel,
  IressPill,
  IressIcon,
  IressTable,
  IressToggle,
  IressStyled,
  IressTabSet,
  IressTab,
  IressSkeleton,
  IressModal,
  IressAlert,
  useToaster,
  IressDivider,
} from '@iress-oss/ids-components';
import { NavigableError } from '@/lib/errors/NavigableError';
import { PageShell } from '@/components/PageShell';
import { AiDisclaimer } from '@/components/AiDisclaimer';
import { postFinalise } from '@/lib/api';
import { useMeeting } from '@/lib/hooks/useMeeting';
import { useSummary } from '@/lib/hooks/useSummary';
import { formatActionCounts } from '@/components/suggestions/utils';
import { AssignModal } from '@/components/suggestions/AssignModal';
import { useSuggestions } from '@/components/suggestions/useSuggestions';
import { buildSuggestionColumns } from '@/components/suggestions/SuggestionColumns';
import { buildActionColumns } from '@/components/suggestions/ActionColumns';
import type { HighlightTarget } from '@/components/suggestions/SourceButton';
import { summaryQueryOptions } from '@/lib/query/summaryQuery';
import { useTenantConfig } from '@/lib/hooks/useTenantConfig';
import { getUsers } from '@/lib/api';

export const Route = createFileRoute('/$id/suggestions')({
  component: SuggestionsPage,
});

function SuggestionsPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { data: meeting } = useMeeting();
  const { data: summaryData, isLoading, transcript } = useSummary();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const {
    data: tenantConfigData,
    isError: configError,
    isLoading: configLoading,
  } = useTenantConfig();

  const state = useSuggestions(summaryData, () => {
    toaster.error('Failed to save change. Please try again.');
  });

  const searchUsers = useCallback(async (query: string) => {
    const all = await getUsers();
    const q = query.toLowerCase();
    return q ? all.filter((u) => u.label.toLowerCase().includes(q)) : all;
  }, []);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [highlight, setHighlight] = useState<HighlightTarget | null>(null);

  const approvedCount = state.statusCountsRef.current.approved;
  const assignedCount = useMemo(
    () => state.actions.filter((a) => a.status === 'assigned').length,
    [state.actions],
  );

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: () => postFinalise(meeting!.id),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: summaryQueryOptions(meeting!.id).queryKey,
      });
      toaster.success('Changes saved successfully');
      void navigate({ to: '/' });
    },
  });

  const handleConfirmSave = useCallback(() => {
    save();
  }, [save]);

  const hasPendingItems =
    state.pendingSuggestionCount > 0 || state.pendingActionCount > 0;

  const handleSave = useCallback(() => {
    setShowSaveModal(true);
  }, []);

  const handleEditFromPreview = useCallback(
    (target: 'summarise' | 'suggestions' | 'actions') => {
      if (target === 'summarise') {
        void navigate({
          to: `/${id}/summarise`,
        });
      }
    },
    [navigate, id],
  );

  // All hooks above — safe to throw/return below.
  if (!summaryData) {
    if (isLoading)
      return (
        <PageShell
          title="Review AI Suggestions"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Summarise', href: `/${id}/summarise` },
            { label: 'Suggestions' },
          ]}
          transcriptEntries={transcript?.content}
          showTranscriptLayout
        >
          <IressPanel p="spacing.5" stretch style={{ minHeight: 0 }}>
            <IressTabSet
              defaultSelected={0}
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
              panelStyle={{ scrollable: 'y', stretch: true }}
            >
              <IressTab label="Suggested updates">
                <IressStack gap="spacing.4" pt="spacing.4">
                  <IressSkeleton mode="text" width="40%" />
                  <IressSkeleton mode="rect" width="100%" height="200px" />
                </IressStack>
              </IressTab>
              <IressTab label="Next actions">
                <IressStack gap="spacing.4" pt="spacing.4">
                  <IressSkeleton mode="text" width="40%" />
                  <IressSkeleton mode="rect" width="100%" height="200px" />
                </IressStack>
              </IressTab>
            </IressTabSet>
          </IressPanel>
        </PageShell>
      );
    throw new NavigableError({
      message: 'No summary data available. Please start from the beginning.',
      to: '/',
      search: { finalise: id },
      buttonLabel: 'Start Over',
    });
  }

  const suggestionColumns = buildSuggestionColumns({
    onStatusChange: state.handleSuggestionStatus,
    onValueCommit: state.handleSuggestedValueCommit,
    categories: Array.from(new Set(state.suggestions.map((s) => s.category))),
    onHighlightSource: setHighlight,
    transcriptEntries: transcript?.content ?? [],
    currentHighlight: highlight,
  });

  const actionColumns = buildActionColumns({
    onAssign: state.handleAssignAction,
    onDismiss: state.handleDismissAction,
    onUndo: state.handleUndoAction,
    onHighlightSource: setHighlight,
    transcriptEntries: transcript?.content ?? [],
    currentHighlight: highlight,
  });

  return (
    <>
      <PageShell
        title="Review AI Suggestions"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Summarise', href: `/${id}/summarise` },
          { label: 'Suggestions' },
        ]}
        transcriptEntries={transcript?.content ?? []}
        highlight={highlight}
        preview={{
          suggestions: state.suggestions,
          actions: state.actions,
          onEdit: handleEditFromPreview,
          hideEditTargets: ['suggestions', 'actions'],
        }}
        actions={
          <IressButton mode="primary" onClick={handleSave} disabled={isSaving}>
            Save
          </IressButton>
        }
      >
        <IressPanel
          p="spacing.5"
          stretch
          style={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IressTabSet
            defaultSelected={0}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
            panelStyle={{ scrollable: 'y', stretch: true }}
          >
            <IressTab
              label={
                <IressInline gap="spacing.2" verticalAlign="middle">
                  <span>Suggested updates</span>
                  {state.pendingSuggestionCount > 0 && (
                    <IressPill>{state.pendingSuggestionCount}</IressPill>
                  )}
                </IressInline>
              }
            >
              <IressStack gap="spacing.4" pt="spacing.4">
                <IressText element="p">
                  AI-detected changes to client data fields based on the meeting
                  discussion. Approve updates to apply them to the client
                  record, or reject to skip. You can edit the suggested value
                  before approving if needed.
                </IressText>
                <IressDivider />
                <IressInline gap="spacing.4" verticalAlign="middle">
                  <IressText color="muted" flex="1">
                    <span ref={state.countsElementRef} />
                  </IressText>
                  <IressInline gap="spacing.2">
                    <IressButton
                      mode="secondary"
                      status="success"
                      onClick={state.handleApproveAll}
                      prepend={<IressIcon name="check" />}
                    >
                      Approve all
                    </IressButton>
                    <IressButton
                      mode="secondary"
                      status="danger"
                      onClick={state.handleRejectAll}
                      prepend={<IressIcon name="close" />}
                    >
                      Reject all
                    </IressButton>
                  </IressInline>
                </IressInline>

                <IressTable
                  caption="Client data suggestions"
                  hiddenCaption
                  columns={suggestionColumns}
                  rows={state.suggestions}
                  empty="No suggested updates for this meeting."
                  compact
                />
              </IressStack>
            </IressTab>

            <IressTab
              label={
                <IressInline gap="spacing.2" verticalAlign="middle">
                  <span>Next actions</span>
                  {state.pendingActionCount > 0 && (
                    <IressPill>{state.pendingActionCount}</IressPill>
                  )}
                </IressInline>
              }
            >
              <IressStack gap="spacing.4" pt="spacing.4">
                <IressText element="p">
                  Follow-up tasks identified from the meeting. Assign a task to
                  create it in Xplan, or dismiss if not needed.
                </IressText>
                <IressDivider />
                <IressInline gap="spacing.4" verticalAlign="middle">
                  <IressText color="muted" flex="1">
                    {formatActionCounts(state.actions)}
                  </IressText>
                  {state.dismissedCount > 0 && (
                    <IressToggle
                      checked={state.showDismissed}
                      onChange={state.setShowDismissed}
                      layout="inline-reverse"
                    >
                      Show dismissed ({state.dismissedCount})
                    </IressToggle>
                  )}
                  <IressButton
                    mode="secondary"
                    status="danger"
                    onClick={state.handleDismissAll}
                  >
                    Dismiss all
                  </IressButton>
                </IressInline>

                <IressTable
                  caption="Next actions"
                  hiddenCaption
                  columns={actionColumns}
                  rows={state.visibleActions}
                  empty="No next actions for this meeting."
                  compact
                  rowProps={(row) => ({
                    bg:
                      row.original.status === 'dismissed'
                        ? 'colour.neutral.30'
                        : undefined,
                  })}
                />
              </IressStack>
            </IressTab>
          </IressTabSet>
          <AiDisclaimer />
        </IressPanel>

        <AssignModal
          action={state.assigningAction}
          show={!!state.assigningAction}
          onClose={() => state.setAssigningAction(null)}
          onAssign={state.handleConfirmAssign}
          tenantConfig={tenantConfigData}
          client={meeting?.client}
          searchUsers={searchUsers}
          configError={configError}
          configLoading={configLoading}
        />

        <IressStyled ref={state.statusRef} aria-live="polite" srOnly />

        {/* Save confirmation modal */}
        <IressModal
          heading="Save changes to Xplan"
          show={showSaveModal}
          onShowChange={(v) => !v && setShowSaveModal(false)}
          size="md"
          footer={
            <IressInline gap="spacing.2" horizontalAlign="right">
              <IressButton
                mode="tertiary"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </IressButton>
              <IressButton
                mode="primary"
                onClick={handleConfirmSave}
                disabled={isSaving}
              >
                Save
              </IressButton>
            </IressInline>
          }
        >
          <IressStack gap="spacing.4">
            {hasPendingItems && (
              <IressAlert status="warning">
                You have{' '}
                {[
                  state.pendingSuggestionCount > 0 &&
                    `${state.pendingSuggestionCount} suggested update${state.pendingSuggestionCount === 1 ? '' : 's'}`,
                  state.pendingActionCount > 0 &&
                    `${state.pendingActionCount} action${state.pendingActionCount === 1 ? '' : 's'}`,
                ]
                  .filter(Boolean)
                  .join(' and ')}{' '}
                still pending. Pending items will not be included in the final
                changes.
              </IressAlert>
            )}
            <IressStack gap="spacing.2">
              <IressText>
                The following changes will be reflected in the client's record
                in Xplan.
              </IressText>
              <IressText>• 1 new file note</IressText>
              <IressText>
                • {approvedCount} client data update
                {approvedCount === 1 ? '' : 's'}
              </IressText>
              <IressText>
                • {assignedCount} new task{assignedCount === 1 ? '' : 's'}
              </IressText>
            </IressStack>
          </IressStack>
        </IressModal>
      </PageShell>
    </>
  );
}
